import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { theme } from '../../Medical_History_Forms/themeprovider';
import { convertUnderscoreToTitle, infoTextStyle, sectionTitleStyle } from '../../../utils/commonUtils';
import { useLoader } from '../../loaderProvider/loader';
import styles from '../oocyteRetrieval/oocyteRetrival.module.css';
import { getHysteroscopy, HysteroscopyType } from './hysteroscopy.helper';
import { DocumentChips, FileViewerModal, UploadedImageType } from '../../common/ImageUploadModal';

interface LaparoscopyViewDetailsProps {
  record: number;
  onBack: () => void;
  onEdit?: () => void;
  canEditOrAdd?: boolean;
  canView?: boolean;
}

const HysteroscopyViewDetails: React.FC<LaparoscopyViewDetailsProps> = ({ record, onBack, onEdit, canEditOrAdd, canView }) => {
  const { showLoader, hideLoader } = useLoader();
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const [retrievalData, setRetrievalData] = useState<HysteroscopyType | null>(null);

  const [openViewer, setOpenViewer] = useState(false);
  const [viewerInitialIndex, setViewerInitialIndex] = useState(0);

  // Convert Files[] to UploadedImageType[] for DocumentChips
  const uploadedFiles: UploadedImageType[] = (retrievalData?.images || []).map((img, index) => {
    // Use file_name from API if available, otherwise extract from URL
    let fileName = img.file_name || `Document_${index + 1}`;
    if (!img.file_name && img.image) {
      try {
        const urlPath = new URL(img.image).pathname;
        const extractedName = urlPath.split('/').pop();
        if (extractedName && extractedName.length > 0) {
          fileName = decodeURIComponent(extractedName);
        }
      } catch {
        // Keep default name if URL parsing fails
      }
    }
    return {
      name: fileName,
      image: img.image || '',
      id: img.id,
      size: img.file_size ? (img.file_size / (1024 * 1024)).toFixed(2) : undefined,
      sizeBytes: img.file_size,
      file_extension: img.file_extension,
      isExisting: true,
    };
  });

  const handleViewFile = (index: number) => {
    setViewerInitialIndex(index);
    setOpenViewer(true);
  };
  
  useEffect(() => {
    if (!record) return;

    showLoader();
    getHysteroscopy(record)
      .then((data) => setRetrievalData(data))
      .catch(() => {
        setRetrievalData(null);
      })
      .finally(() => hideLoader());
  }, [record, showLoader, hideLoader]);

  const fields = [
    { label: tGeneral('date'), value: retrievalData?.date },
    { label: tSurgery('surgeonName'), value: retrievalData?.surgeon_name },
    { label: tSurgery('assistantSurgeon'), value: retrievalData?.assistant_surgeon_name },
    { label: tSurgery('anesthetist'), value: retrievalData?.anesthetist_name },
    { label: tSurgery('procedureDone'), value: retrievalData?.procedure_done_label },
    { label: tSurgery('procedureDurationMin'), value: retrievalData?.procedure_duration },
    { label: tSurgery('cervicalOs'), value: retrievalData?.cervical_os_label },
    { label: tSurgery('endocervicalConal'), value: convertUnderscoreToTitle(retrievalData?.endocervical_conal) },
    { label: tSurgery('uterusCervicalLengthMm'), value: retrievalData?.uterus_cervical_length },
    { label: tSurgery('cavity'), value: retrievalData?.cavity_label },
    { label: tSurgery('endometrium'), value: retrievalData?.endometrium_label },
    { label: tSurgery('rightOstia'), value: retrievalData?.right_ostia },
    { label: tSurgery('leftOstia'), value: retrievalData?.left_ostia }, 
    { label: tSurgery('ebHpe'), value: retrievalData?.eb_hpe },
    { label: tSurgery('otherDetails'), value: retrievalData?.other_details },
    { label: tGeneral('remark'), value: retrievalData?.remarks },
    { label: tSurgery('doneBy'), value: retrievalData?.done_by_name },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4} mb={3}>
          {fields.map((field, index) => (
            field.value && (
              <Grid key={index} size={3} sx={{ textAlign: 'left' }}>
                <Typography variant="body1" sx={sectionTitleStyle}>
                  {field.label}
                </Typography>
                <Typography variant="body2" sx={infoTextStyle}>
                  {field.value}
                </Typography>
              </Grid>
            )
          ))}
        </Grid> 

        {/* Document Chips for uploaded files */}
        {uploadedFiles.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="body1" sx={{ ...sectionTitleStyle, mb: 1 }}>
              Documents
            </Typography>
            <DocumentChips
              files={uploadedFiles}
              onViewClick={handleViewFile}
            />
          </Box>
        )}

        {/* File Viewer Modal */}
        <FileViewerModal
          open={openViewer}
          onClose={() => setOpenViewer(false)}
          files={uploadedFiles}
          initialIndex={viewerInitialIndex}
          title={tGeneral('preview')}
        />

        <Box className={styles.customButtonDiv}>
          <button
            type="button"
            className={styles.resetButton}
            onClick={onBack}
          >
            {tGeneral('backToList')}
          </button>

          {canEditOrAdd && canView && (
            <button
              type="button"
              className={styles.customButton}
              onClick={onEdit}
            >
              <span style={{ fontWeight: 'bold' }}>
                {tGeneral('edit')}
              </span>
            </button>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default HysteroscopyViewDetails;
